/**
 * Layer chips of a summed motion column (W2-04a; DESIGN.md §4.7, §5.6.3; Full level): one chip per layer of the
 * property (eventLayers index, RPE shows 4; imported charts may have more) plus "add a layer".
 *
 *  - labelled states, never colour alone: a filled dot = the layer has keyframes, a hollow dot = empty; the chip
 *    of the layer being edited is pressed (aria-pressed); muted chips are struck through with a muted icon, a soloed
 *    chip carries a headphones icon; every state is also in the tooltip and the accessible name;
 *  - click = edit that layer (editor `activeLayer`; every summed column follows, like RPE's layer buttons);
 *  - the menu of the active chip: Rename (inline field, editor-only names), Mute / Solo in the preview (never
 *    exported: the "Preview differs from export" banner shows), Move selected segments here, Delete layer;
 *  - narrow columns (W3-05, UX-034): when the chips do not fit, one "L1 of 4" chip keeps the edited layer visible and
 *    its menu lists every layer (pick one), "add a layer" and the edited layer's actions. The row switches back as
 *    soon as the chips fit again (measured on resize).
 */

import { useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type RefObject } from 'react';
import type { LineId, LineProp } from '../../core/model/types';
import { useT } from '../../i18n/useT';
import { Icon } from '../../ui/Icon';
import { Menu } from '../../ui/Menu';
import type { MenuItem } from '../../ui/types';
import { MAX_UI_LAYERS } from '../../core/commands/layers';
import {
  addLayer,
  deleteLayer,
  isMuted,
  isSolo,
  layerList,
  moveSelectionToLayer,
  renameLayer,
  setActiveLayer,
  toggleMute,
  toggleSolo,
} from './motionCommands';
import { joinList } from './format';
import { m } from './motion.messages';
import s from './Motion.module.css';
import type { Services } from './txn';

export interface LayerChipsProps {
  readonly services: Services;
  readonly line: LineId;
  readonly prop: LineProp;
  /** Property label for accessible names ("Rotation"). */
  readonly propLabel: string;
  /** Re-render key (document / editor version). */
  readonly version: number;
}

/**
 * Whether a row's content overflows it: true once the content does not fit; false again once the row is at least as
 * wide as the content measured when it overflowed. Re-checked on resize and when `key` changes.
 */
function useOverflowSwitch(key: string): readonly [boolean, RefObject<HTMLDivElement | null>] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [overflow, setOverflow] = useState(false);
  const needed = useRef(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el === null) return undefined;
    const check = (): void => {
      if (!overflow) {
        if (el.scrollWidth > el.clientWidth + 1) {
          needed.current = el.scrollWidth;
          setOverflow(true);
        }
      } else if (el.clientWidth >= needed.current) setOverflow(false);
    };
    check();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [overflow, key]);
  return [overflow, ref];
}

export function LayerChips(props: LayerChipsProps) {
  const { services, line, prop, propLabel } = props;
  const t = useT();
  const [renaming, setRenaming] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  const store = services.store;
  const st0 = services.editor.getState();
  const layersKey =
    store === null
      ? ''
      : `${layerList(store, line, prop, [st0.activeLayer]).join(',')}|${(store.doc.lines.get(line)?.editor.layerNames ?? []).join('\u0000')}|${String(renaming)}`;
  const [narrow, rowRef] = useOverflowSwitch(layersKey);
  if (store === null) return null;
  const st = services.editor.getState();
  const active = st.activeLayer;
  const layers = layerList(store, line, prop, [active]);
  const inUse = new Set(layerList(store, line, prop).filter((l) => store.idx.track(line, prop, l) !== undefined));
  const names = store.doc.lines.get(line)?.editor.layerNames ?? [];
  const canAdd = inUse.size < MAX_UI_LAYERS;

  const commitRename = (layer: number): void => {
    renameLayer(services, line, layer, draft);
    setRenaming(null);
  };
  const onRenameKey = (e: ReactKeyboardEvent<HTMLInputElement>, layer: number): void => {
    e.stopPropagation();
    if (e.key === 'Enter') commitRename(layer);
    else if (e.key === 'Escape') setRenaming(null);
  };

  /** Actions of the edited layer (its chip's menu, and the compact menu). */
  const activeItems = (layer: number): MenuItem[] => {
    const has = inUse.has(layer);
    const muted = isMuted(services, line, prop, layer);
    const solo = isSolo(services, { line, prop, layer });
    const name = names[layer] ?? null;
    const col = { line, prop, layer };
    return [
      {
        kind: 'action',
        label: t(m.layerRename),
        icon: 'Pencil',
        onSelect: () => {
          setDraft(name ?? '');
          setRenaming(layer);
        },
      },
      {
        kind: 'action',
        label: t(muted ? m.layerUnmute : m.layerMute),
        icon: muted ? 'Volume2' : 'VolumeX',
        onSelect: () => {
          toggleMute(services, col);
        },
      },
      {
        kind: 'action',
        label: t(solo ? m.layerUnsolo : m.layerSolo),
        icon: 'Headphones',
        disabledReason: layers.length < 2 ? { key: m.needLayered } : null,
        onSelect: () => {
          toggleSolo(services, col);
        },
      },
      {
        kind: 'action',
        label: t(m.layerMoveHere),
        icon: 'Layers',
        onSelect: () => {
          moveSelectionToLayer(services, layer);
        },
      },
      { kind: 'separator' },
      {
        kind: 'action',
        label: t(m.layerDelete),
        icon: 'Trash2',
        disabledReason: has ? null : { key: m.layerChipEmpty, params: { n: layer } },
        onSelect: () => {
          void deleteLayer(services, col);
        },
      },
    ];
  };

  const chipLabel = (layer: number): string => {
    const name = names[layer] ?? null;
    return name === null ? t(m.layerChip, { n: layer }) : t(m.layerChipNamed, { name });
  };

  if (narrow && renaming === null) {
    // One chip for the edited layer; the menu picks another one (the edited layer never scrolls out of view).
    const items: MenuItem[] = [
      ...layers.map((layer): MenuItem => ({
        kind: 'action',
        label: chipLabel(layer),
        icon: layer === active ? 'Check' : inUse.has(layer) ? 'CircleDot' : 'Circle',
        onSelect: () => {
          if (layer !== active) setActiveLayer(services, layer);
        },
      })),
      {
        kind: 'action',
        label: t(m.layerAdd),
        icon: 'Plus',
        disabledReason: canAdd ? null : { key: m.layerLimit },
        onSelect: () => {
          addLayer(services, line, prop);
        },
      },
      { kind: 'separator' },
      ...activeItems(active),
    ];
    const muted = isMuted(services, line, prop, active);
    return (
      <div ref={rowRef} className={s.chipRow} role="toolbar" aria-label={t(m.layerChipsLabel, { prop: propLabel })}>
        <Menu
          trigger={
            <button
              type="button"
              className={s.chip}
              aria-pressed={true}
              data-muted={muted}
              aria-label={t(m.layerChipsMenu, { n: active, count: layers.length })}
              title={t(m.layerChipsMenu, { n: active, count: layers.length })}
              onMouseDown={(e) => {
                e.preventDefault();
              }}
            >
              <span className={s.dot} data-has={inUse.has(active)} aria-hidden="true" />
              {layers.length > 1
                ? t(m.layerChipCompact, { n: active, count: layers.length })
                : t(m.layerChip, { n: active })}
              <Icon name="ChevronDown" size={10} />
            </button>
          }
          items={items}
        />
      </div>
    );
  }

  return (
    <div ref={rowRef} className={s.chipRow} role="toolbar" aria-label={t(m.layerChipsLabel, { prop: propLabel })}>
      {layers.map((layer) => {
        const has = inUse.has(layer);
        const muted = isMuted(services, line, prop, layer);
        const solo = isSolo(services, { line, prop, layer });
        const name = names[layer] ?? null;
        const label = name === null ? t(m.layerChip, { n: layer }) : t(m.layerChipNamed, { name });
        const state = joinList(t, [
          layer === active
            ? t(m.layerChipActive, { n: layer })
            : has
              ? t(m.layerChipHas, { n: layer })
              : t(m.layerChipEmpty, { n: layer }),
          ...(muted ? [t(m.layerChipMuted, { n: layer })] : []),
          ...(solo ? [t(m.layerChipSolo, { n: layer })] : []),
        ]);
        if (renaming === layer) {
          return (
            <input
              key={layer}
              className={s.chipInput}
              aria-label={t(m.layerRenamePrompt, { n: layer })}
              value={draft}
              autoFocus
              onChange={(e) => {
                setDraft(e.target.value);
              }}
              onKeyDown={(e) => {
                onRenameKey(e, layer);
              }}
              onBlur={() => {
                commitRename(layer);
              }}
            />
          );
        }
        const chip = (
          <button
            key={layer}
            type="button"
            className={s.chip}
            aria-pressed={layer === active}
            data-muted={muted}
            title={state}
            aria-label={t(m.layerChipAria, { label, state })}
            onMouseDown={(e) => {
              e.preventDefault();
            }}
            onClick={() => {
              if (layer !== active) setActiveLayer(services, layer);
            }}
          >
            <span className={s.dot} data-has={has} aria-hidden="true" />
            {label}
            {muted ? <Icon name="VolumeX" size={10} /> : null}
            {solo ? <Icon name="Headphones" size={10} /> : null}
          </button>
        );
        if (layer !== active) return chip;
        const items = activeItems(layer);
        return (
          <span key={layer} className={s.chipGroup}>
            {chip}
            <Menu
              trigger={
                <button
                  type="button"
                  className={s.iconBtn}
                  aria-label={t(m.layerMenu, { n: layer })}
                  onMouseDown={(e) => {
                    e.preventDefault();
                  }}
                >
                  <Icon name="ChevronDown" size={10} />
                </button>
              }
              items={items}
            />
          </span>
        );
      })}
      <button
        type="button"
        className={s.iconBtn}
        aria-label={t(m.layerAdd)}
        title={canAdd ? t(m.layerAdd) : t(m.layerLimit)}
        aria-disabled={!canAdd}
        onMouseDown={(e) => {
          e.preventDefault();
        }}
        onClick={() => {
          if (canAdd) addLayer(services, line, prop);
        }}
      >
        <Icon name="Plus" size={11} />
      </button>
    </div>
  );
}
